/**
 * ParticleSystem: two pooled THREE.Points (additive + normal blending) driven by a
 * small ShaderMaterial, a pooled additive tracer LineSegments, and 3 pooled
 * PointLights for explosion flashes. Everything is CPU-integrated into
 * DynamicDraw BufferAttributes; no per-frame allocations.
 */
import * as THREE from 'three';
import { G } from './ctx';
import type { IParticleSystem, ParticleKind } from './types';
import { makeRng } from './util';

const POOL_MAX = 3000;
const TRACER_MAX = 16;
const TRACER_LIFE = 0.08;
const GROUND_Y = 0.02;

/** ground behaviour when a particle reaches y = 0 */
const GROUND_NONE = 0;
const GROUND_STOP = 1;
const GROUND_BOUNCE = 2;

const TEX_SOFT = 0;
const TEX_PUFF = 1;

// ------------------------------------------------------------------ texture ----

function makeSpriteAtlas(): THREE.CanvasTexture {
  const w = 256;
  const h = 128;
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext('2d')!;
  ctx.clearRect(0, 0, w, h);
  // tile 0: soft round sprite
  {
    const cx = 64;
    const cy = 64;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 62);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.85)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.28)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
  }
  // tile 1: puffy cloud (several soft blobs)
  {
    const rng = makeRng(99);
    const cx = 192;
    const cy = 64;
    const blobs = 9;
    for (let i = 0; i < blobs; i++) {
      const ang = (i / blobs) * Math.PI * 2 + rng() * 0.8;
      const rad = i === 0 ? 0 : 14 + rng() * 14;
      const bx = cx + Math.cos(ang) * rad;
      const by = cy + Math.sin(ang) * rad;
      const r = i === 0 ? 40 : 20 + rng() * 14;
      const g = ctx.createRadialGradient(bx, by, 0, bx, by, r);
      g.addColorStop(0, 'rgba(255,255,255,0.55)');
      g.addColorStop(0.5, 'rgba(255,255,255,0.22)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(128, 0, 128, 128);
    }
    // keep tile edges fully transparent
    const img = ctx.getImageData(128, 0, 128, 128);
    const d = img.data;
    for (let y = 0; y < 128; y++) {
      for (let x = 0; x < 128; x++) {
        const dx = x - 64;
        const dy = y - 64;
        const rr = Math.sqrt(dx * dx + dy * dy) / 64;
        const fade = rr < 0.7 ? 1 : Math.max(0, 1 - (rr - 0.7) / 0.3);
        const k = (y * 128 + x) * 4 + 3;
        d[k] = Math.round(d[k] * fade);
      }
    }
    ctx.putImageData(img, 128, 0);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

// ------------------------------------------------------------------ shader ----

const VERT = /* glsl */ `
uniform float uScale;
attribute float aT;
attribute vec4 aParams;
attribute vec3 aColor;
attribute vec3 aColor2;
attribute vec2 aMisc;
varying float vAlpha;
varying vec3 vColor;
varying vec2 vMisc;
#include <fog_pars_vertex>
void main() {
  float t = clamp(aT, 0.0, 1.0);
  float size = mix(aParams.x, aParams.y, t);
  float fadeIn = smoothstep(0.0, 0.08, t + 0.02);
  vAlpha = aParams.z * pow(1.0 - t, aParams.w) * fadeIn;
  vColor = mix(aColor, aColor2, t);
  vMisc = aMisc;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = max(1.0, size * uScale / max(0.15, -mvPosition.z));
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uMap;
varying float vAlpha;
varying vec3 vColor;
varying vec2 vMisc;
#include <fog_pars_fragment>
void main() {
  vec2 uv = gl_PointCoord - 0.5;
  float c = cos(vMisc.y);
  float s = sin(vMisc.y);
  uv = vec2(uv.x * c - uv.y * s, uv.x * s + uv.y * c) + 0.5;
  uv = clamp(uv, 0.0, 1.0);
  uv.x = (uv.x + vMisc.x) * 0.5;
  float m = texture2D(uMap, uv).a;
  float a = m * vAlpha;
  #ifdef USE_FOG
    #ifdef FOG_EXP2
      float fogFactor = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
    #else
      float fogFactor = smoothstep(fogNear, fogFar, vFogDepth);
    #endif
    a *= 1.0 - fogFactor;
  #endif
  if (a < 0.003) discard;
  #ifdef ADDITIVE
    gl_FragColor = vec4(vColor * a, a);
  #else
    gl_FragColor = vec4(vColor, a);
  #endif
}
`;

// -------------------------------------------------------------------- pool ----

class Pool {
  readonly points: THREE.Points;
  readonly material: THREE.ShaderMaterial;
  n = 0;
  // GPU-facing attribute arrays
  readonly pos: Float32Array;
  readonly aT: Float32Array;
  readonly aParams: Float32Array;
  readonly aColor: Float32Array;
  readonly aColor2: Float32Array;
  readonly aMisc: Float32Array;
  // CPU-only state
  readonly vel: Float32Array;
  readonly age: Float32Array;
  readonly life: Float32Array;
  readonly grav: Float32Array;
  readonly drag: Float32Array;
  readonly spin: Float32Array;
  readonly ground: Uint8Array;
  private readonly attrs: THREE.BufferAttribute[];
  private readonly geo: THREE.BufferGeometry;

  constructor(additive: boolean, map: THREE.Texture) {
    this.pos = new Float32Array(POOL_MAX * 3);
    this.aT = new Float32Array(POOL_MAX);
    this.aParams = new Float32Array(POOL_MAX * 4);
    this.aColor = new Float32Array(POOL_MAX * 3);
    this.aColor2 = new Float32Array(POOL_MAX * 3);
    this.aMisc = new Float32Array(POOL_MAX * 2);
    this.vel = new Float32Array(POOL_MAX * 3);
    this.age = new Float32Array(POOL_MAX);
    this.life = new Float32Array(POOL_MAX);
    this.grav = new Float32Array(POOL_MAX);
    this.drag = new Float32Array(POOL_MAX);
    this.spin = new Float32Array(POOL_MAX);
    this.ground = new Uint8Array(POOL_MAX);

    const geo = new THREE.BufferGeometry();
    const mk = (arr: Float32Array, size: number, name: string) => {
      const a = new THREE.BufferAttribute(arr, size);
      a.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute(name, a);
      return a;
    };
    this.attrs = [
      mk(this.pos, 3, 'position'),
      mk(this.aT, 1, 'aT'),
      mk(this.aParams, 4, 'aParams'),
      mk(this.aColor, 3, 'aColor'),
      mk(this.aColor2, 3, 'aColor2'),
      mk(this.aMisc, 2, 'aMisc'),
    ];
    geo.setDrawRange(0, 0);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1e6);
    this.geo = geo;

    this.material = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, { uMap: { value: null }, uScale: { value: 400 } }]),
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      fog: true,
      defines: additive ? { ADDITIVE: 1 } : {},
    });
    this.material.uniforms.uMap.value = map;
    this.points = new THREE.Points(geo, this.material);
    this.points.frustumCulled = false;
    this.points.renderOrder = additive ? 20 : 10;
    this.points.visible = false;
    this.points.name = additive ? 'particles-additive' : 'particles-normal';
  }

  /** returns the slot index or -1 when the pool is full (oldest is recycled instead) */
  alloc(): number {
    if (this.n >= POOL_MAX) {
      // recycle the particle closest to its end of life
      let best = 0;
      let bt = -1;
      for (let i = 0; i < this.n; i += 7) {
        const t = this.age[i] / this.life[i];
        if (t > bt) {
          bt = t;
          best = i;
        }
      }
      return best;
    }
    return this.n++;
  }

  private remove(i: number): void {
    const last = this.n - 1;
    if (i !== last) {
      const p = this.pos, v = this.vel, c = this.aColor, c2 = this.aColor2, m = this.aMisc, q = this.aParams;
      p[i * 3] = p[last * 3];
      p[i * 3 + 1] = p[last * 3 + 1];
      p[i * 3 + 2] = p[last * 3 + 2];
      v[i * 3] = v[last * 3];
      v[i * 3 + 1] = v[last * 3 + 1];
      v[i * 3 + 2] = v[last * 3 + 2];
      c[i * 3] = c[last * 3];
      c[i * 3 + 1] = c[last * 3 + 1];
      c[i * 3 + 2] = c[last * 3 + 2];
      c2[i * 3] = c2[last * 3];
      c2[i * 3 + 1] = c2[last * 3 + 1];
      c2[i * 3 + 2] = c2[last * 3 + 2];
      m[i * 2] = m[last * 2];
      m[i * 2 + 1] = m[last * 2 + 1];
      q[i * 4] = q[last * 4];
      q[i * 4 + 1] = q[last * 4 + 1];
      q[i * 4 + 2] = q[last * 4 + 2];
      q[i * 4 + 3] = q[last * 4 + 3];
      this.aT[i] = this.aT[last];
      this.age[i] = this.age[last];
      this.life[i] = this.life[last];
      this.grav[i] = this.grav[last];
      this.drag[i] = this.drag[last];
      this.spin[i] = this.spin[last];
      this.ground[i] = this.ground[last];
    }
    this.n = last;
  }

  update(dt: number): void {
    const p = this.pos, v = this.vel;
    let i = 0;
    while (i < this.n) {
      const age = this.age[i] + dt;
      if (age >= this.life[i]) {
        this.remove(i);
        continue;
      }
      this.age[i] = age;
      this.aT[i] = age / this.life[i];
      const i3 = i * 3;
      const k = Math.max(0, 1 - this.drag[i] * dt);
      let vx = v[i3] * k;
      let vy = (v[i3 + 1] - this.grav[i] * dt) * k;
      let vz = v[i3 + 2] * k;
      let x = p[i3] + vx * dt;
      let y = p[i3 + 1] + vy * dt;
      let z = p[i3 + 2] + vz * dt;
      const g = this.ground[i];
      if (g !== GROUND_NONE && y < GROUND_Y) {
        y = GROUND_Y;
        if (g === GROUND_STOP) {
          vy = 0;
          vx *= 0.2;
          vz *= 0.2;
        } else {
          vy = -vy * 0.45;
          vx *= 0.7;
          vz *= 0.7;
        }
      }
      v[i3] = vx;
      v[i3 + 1] = vy;
      v[i3 + 2] = vz;
      p[i3] = x;
      p[i3 + 1] = y;
      p[i3 + 2] = z;
      const sp = this.spin[i];
      if (sp !== 0) this.aMisc[i * 2 + 1] += sp * dt;
      i++;
    }
    const n = this.n;
    this.geo.setDrawRange(0, n);
    this.points.visible = n > 0;
    if (n > 0) {
      for (const a of this.attrs) {
        a.clearUpdateRanges();
        a.addUpdateRange(0, n * a.itemSize);
        a.needsUpdate = true;
      }
    }
  }
}

// ------------------------------------------------------------------ system ----

export class ParticleSystem implements IParticleSystem {
  /** debug knob: slows particle time (screenshots). 1 = realtime. */
  timeScale = 1;
  /** when/where the last composite explosion was spawned (lets WeaponSystem avoid doubling visuals) */
  lastExplosionT = -1;
  lastExplosionX = 0;
  lastExplosionZ = 0;

  private readonly scene: THREE.Scene;
  private readonly add: Pool;
  private readonly nrm: Pool;
  private readonly rng = makeRng(4242);
  private readonly tmpSize = new THREE.Vector2();

  // tracers
  private readonly tracerLine: THREE.LineSegments;
  private readonly tracerPos: Float32Array;
  private readonly tracerCol: Float32Array;
  private readonly tracerAge = new Float32Array(TRACER_MAX).fill(99);
  private tracerNext = 0;
  private tracerAlive = 0;

  // explosion lights
  private readonly lights: THREE.PointLight[] = [];
  private readonly lightAge = new Float32Array(3).fill(99);
  private readonly lightPeak = new Float32Array(3);

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    const atlas = makeSpriteAtlas();
    this.add = new Pool(true, atlas);
    this.nrm = new Pool(false, atlas);
    scene.add(this.nrm.points);
    scene.add(this.add.points);

    // tracers: one LineSegments, vertex colours fade to black (additive => invisible)
    this.tracerPos = new Float32Array(TRACER_MAX * 6);
    this.tracerCol = new Float32Array(TRACER_MAX * 6);
    const tg = new THREE.BufferGeometry();
    const tp = new THREE.BufferAttribute(this.tracerPos, 3);
    tp.setUsage(THREE.DynamicDrawUsage);
    const tc = new THREE.BufferAttribute(this.tracerCol, 3);
    tc.setUsage(THREE.DynamicDrawUsage);
    tg.setAttribute('position', tp);
    tg.setAttribute('color', tc);
    tg.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    const tm = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
    this.tracerLine = new THREE.LineSegments(tg, tm);
    this.tracerLine.frustumCulled = false;
    this.tracerLine.renderOrder = 21;
    this.tracerLine.visible = false;
    this.tracerLine.name = 'tracers';
    scene.add(this.tracerLine);

    // explosion flash lights: permanently in the scene (intensity 0) so the light
    // count never changes and materials never recompile.
    for (let i = 0; i < 3; i++) {
      const l = new THREE.PointLight(0xffa447, 0, 40, 2);
      l.castShadow = false;
      l.name = 'explosion-light-' + i;
      scene.add(l);
      this.lights.push(l);
    }
  }

  // ----------------------------------------------------------------- emit ----

  private spawn(
    pool: Pool,
    x: number, y: number, z: number,
    vx: number, vy: number, vz: number,
    life: number,
    size0: number, size1: number, alpha: number, alphaPow: number,
    r0: number, g0: number, b0: number,
    r1: number, g1: number, b1: number,
    tex: number, rot: number, spin: number,
    grav: number, drag: number, ground: number,
  ): void {
    const i = pool.alloc();
    const i3 = i * 3;
    pool.pos[i3] = x;
    pool.pos[i3 + 1] = y;
    pool.pos[i3 + 2] = z;
    pool.vel[i3] = vx;
    pool.vel[i3 + 1] = vy;
    pool.vel[i3 + 2] = vz;
    pool.age[i] = 0;
    pool.life[i] = life;
    pool.aT[i] = 0;
    const i4 = i * 4;
    pool.aParams[i4] = size0;
    pool.aParams[i4 + 1] = size1;
    pool.aParams[i4 + 2] = alpha;
    pool.aParams[i4 + 3] = alphaPow;
    pool.aColor[i3] = r0;
    pool.aColor[i3 + 1] = g0;
    pool.aColor[i3 + 2] = b0;
    pool.aColor2[i3] = r1;
    pool.aColor2[i3 + 1] = g1;
    pool.aColor2[i3 + 2] = b1;
    pool.aMisc[i * 2] = tex;
    pool.aMisc[i * 2 + 1] = rot;
    pool.spin[i] = spin;
    pool.grav[i] = grav;
    pool.drag[i] = drag;
    pool.ground[i] = ground;
  }

  private rnd(lo: number, hi: number): number {
    return lo + this.rng() * (hi - lo);
  }

  emit(kind: ParticleKind, x: number, y: number, z: number, count?: number, dirX?: number, dirY?: number, dirZ?: number): void {
    const hasDir = dirX !== undefined && dirY !== undefined && dirZ !== undefined;
    let dx = hasDir ? dirX : 0;
    let dy = hasDir ? dirY : 0;
    let dz = hasDir ? dirZ : 0;
    if (hasDir) {
      const l = Math.hypot(dx, dy, dz);
      if (l > 1e-4) {
        dx /= l;
        dy /= l;
        dz /= l;
      } else {
        dx = dy = dz = 0;
      }
    }
    const R = this.rng;
    switch (kind) {
      case 'smoke': {
        const n = count ?? 4;
        for (let i = 0; i < n; i++) {
          const g = this.rnd(0.36, 0.46);
          this.spawn(
            this.nrm,
            x + this.rnd(-0.25, 0.25), y + this.rnd(-0.1, 0.1), z + this.rnd(-0.25, 0.25),
            dx * 0.7 + this.rnd(-0.35, 0.35), 0.9 + dy * 0.7 + this.rnd(0, 0.7), dz * 0.7 + this.rnd(-0.35, 0.35),
            this.rnd(2, 3),
            this.rnd(0.45, 0.7), this.rnd(1.6, 2.4), 0.5, 1.3,
            g, g, g + 0.02, g + 0.2, g + 0.2, g + 0.22,
            TEX_PUFF, R() * 6.28, this.rnd(-0.5, 0.5),
            -0.35, 0.6, GROUND_NONE,
          );
        }
        break;
      }
      case 'fire': {
        const n = count ?? 3;
        for (let i = 0; i < n; i++) {
          this.spawn(
            this.add,
            x + this.rnd(-0.2, 0.2), y + this.rnd(-0.1, 0.1), z + this.rnd(-0.2, 0.2),
            dx * 0.8 + this.rnd(-0.5, 0.5), 1.2 + dy + this.rnd(0, 1.2), dz * 0.8 + this.rnd(-0.5, 0.5),
            this.rnd(0.35, 0.65),
            this.rnd(0.4, 0.6), this.rnd(0.08, 0.16), 0.95, 1.1,
            2.4, 1.1, 0.25, 1.3, 0.22, 0.03,
            TEX_PUFF, R() * 6.28, this.rnd(-2, 2),
            -1.6, 1.6, GROUND_NONE,
          );
        }
        break;
      }
      case 'explosion':
        this.explosion(x, y, z, count ?? 1);
        break;
      case 'spark':
        this.sparks(x, y, z, count ?? 12, dx, dy, dz, 4, 10, 0.35, 0.8);
        break;
      case 'blood': {
        const n = count ?? 10;
        for (let i = 0; i < n; i++) {
          const sp = this.rnd(1.2, 4);
          const d = this.rnd(0, 0.9);
          this.spawn(
            this.nrm,
            x, y, z,
            (dx * 0.6 + this.rnd(-0.5, 0.5)) * sp * (1 - d), (dy * 0.6 + this.rnd(0, 0.9)) * sp, (dz * 0.6 + this.rnd(-0.5, 0.5)) * sp * (1 - d),
            this.rnd(0.35, 0.7),
            this.rnd(0.07, 0.16), this.rnd(0.18, 0.3), 0.92, 0.8,
            0.42, 0.03, 0.04, 0.22, 0.015, 0.02,
            TEX_SOFT, 0, 0,
            9, 0.8, GROUND_STOP,
          );
        }
        break;
      }
      case 'muzzle': {
        const n = count ?? 3;
        // bright core (puff texture for an irregular, gassy edge)
        this.spawn(
          this.add,
          x, y, z,
          dx * 1.2, dy * 1.2, dz * 1.2,
          0.07,
          0.2, 0.34, 1, 0.6,
          4.5, 3.4, 1.8, 2.2, 1.1, 0.4,
          TEX_PUFF, R() * 6.28, 0,
          0, 0, GROUND_NONE,
        );
        // forward tongues of flame
        for (let i = 1; i < n; i++) {
          this.spawn(
            this.add,
            x + dx * 0.12 * i, y + dy * 0.12 * i, z + dz * 0.12 * i,
            dx * this.rnd(2, 5) + this.rnd(-0.4, 0.4), dy * this.rnd(2, 5) + this.rnd(-0.4, 0.4), dz * this.rnd(2, 5) + this.rnd(-0.4, 0.4),
            this.rnd(0.05, 0.09),
            this.rnd(0.09, 0.15), this.rnd(0.16, 0.24), 0.9, 0.7,
            3.2, 2.0, 0.7, 1.4, 0.5, 0.1,
            TEX_PUFF, R() * 6.28, 0,
            0, 2, GROUND_NONE,
          );
        }
        // a wisp of smoke from the barrel
        this.spawn(
          this.nrm,
          x + dx * 0.2, y + dy * 0.2, z + dz * 0.2,
          dx * 0.5 + this.rnd(-0.2, 0.2), 0.6 + this.rnd(0, 0.3), dz * 0.5 + this.rnd(-0.2, 0.2),
          this.rnd(0.5, 0.8),
          0.12, 0.5, 0.28, 1.2,
          0.6, 0.6, 0.62, 0.7, 0.7, 0.72,
          TEX_PUFF, R() * 6.28, this.rnd(-1, 1),
          -0.4, 1.5, GROUND_NONE,
        );
        break;
      }
      case 'dust': {
        const n = count ?? 6;
        for (let i = 0; i < n; i++) {
          this.spawn(
            this.nrm,
            x + this.rnd(-0.2, 0.2), y + 0.05, z + this.rnd(-0.2, 0.2),
            dx * 0.8 + this.rnd(-0.8, 0.8), 0.4 + dy * 0.5 + this.rnd(0, 0.5), dz * 0.8 + this.rnd(-0.8, 0.8),
            this.rnd(0.7, 1.2),
            this.rnd(0.25, 0.4), this.rnd(0.8, 1.2), 0.34, 1.0,
            0.72, 0.66, 0.54, 0.66, 0.6, 0.5,
            TEX_PUFF, R() * 6.28, this.rnd(-1, 1),
            0.3, 1.8, GROUND_STOP,
          );
        }
        break;
      }
      case 'debris': {
        const n = count ?? 8;
        for (let i = 0; i < n; i++) {
          const sp = this.rnd(3, 9);
          const ax = this.rnd(-1, 1), ay = this.rnd(0.4, 1.2), az = this.rnd(-1, 1);
          const l = Math.hypot(ax, ay, az) || 1;
          const s = this.rnd(0.07, 0.16);
          const shade = this.rnd(0.09, 0.2);
          this.spawn(
            this.nrm,
            x, y, z,
            (ax / l + dx * 0.5) * sp, (ay / l + dy * 0.5) * sp, (az / l + dz * 0.5) * sp,
            this.rnd(0.8, 1.6),
            s, s, 1, 0.5,
            shade, shade * 0.9, shade * 0.8, shade * 0.6, shade * 0.55, shade * 0.5,
            TEX_SOFT, R() * 6.28, this.rnd(-8, 8),
            12, 0.2, GROUND_BOUNCE,
          );
        }
        break;
      }
      case 'water': {
        const n = count ?? 12;
        for (let i = 0; i < n; i++) {
          const sp = this.rnd(2, 5);
          const ax = this.rnd(-0.6, 0.6), az = this.rnd(-0.6, 0.6);
          this.spawn(
            this.nrm,
            x, y, z,
            (ax + dx * 0.4) * sp, (0.8 + this.rnd(0, 0.6) + dy * 0.4) * sp, (az + dz * 0.4) * sp,
            this.rnd(0.5, 0.9),
            this.rnd(0.12, 0.2), this.rnd(0.25, 0.4), 0.72, 1.0,
            0.82, 0.9, 1.0, 0.6, 0.76, 0.92,
            TEX_SOFT, 0, 0,
            9.8, 0.4, GROUND_STOP,
          );
        }
        break;
      }
      case 'cash': {
        const n = count ?? 16;
        for (let i = 0; i < n; i++) {
          this.spawn(
            this.add,
            x + this.rnd(-0.35, 0.35), y + this.rnd(0, 0.5), z + this.rnd(-0.35, 0.35),
            this.rnd(-0.6, 0.6), this.rnd(1, 2.2), this.rnd(-0.6, 0.6),
            this.rnd(0.7, 1.1),
            this.rnd(0.1, 0.16), 0.03, 1, 0.7,
            0.7, 2.6, 1.0, 0.2, 1.3, 0.45,
            TEX_SOFT, 0, 0,
            -0.5, 1.0, GROUND_NONE,
          );
        }
        break;
      }
    }
  }

  private sparks(x: number, y: number, z: number, n: number, dx: number, dy: number, dz: number, spLo: number, spHi: number, lifeLo: number, lifeHi: number): void {
    const hasDir = dx !== 0 || dy !== 0 || dz !== 0;
    for (let i = 0; i < n; i++) {
      let ax = this.rnd(-1, 1), ay = this.rnd(-0.3, 1), az = this.rnd(-1, 1);
      if (hasDir) {
        ax = dx + ax * 0.55;
        ay = dy + ay * 0.55;
        az = dz + az * 0.55;
      }
      const l = Math.hypot(ax, ay, az) || 1;
      const sp = this.rnd(spLo, spHi);
      const s = this.rnd(0.05, 0.1);
      this.spawn(
        this.add,
        x, y, z,
        (ax / l) * sp, (ay / l) * sp, (az / l) * sp,
        this.rnd(lifeLo, lifeHi),
        s, s * 0.5, 1, 0.9,
        3.2, 2.5, 1.3, 2.2, 0.8, 0.15,
        TEX_SOFT, 0, 0,
        9.8, 0.5, GROUND_BOUNCE,
      );
    }
  }

  explosion(x: number, y: number, z: number, size = 1): void {
    const R = this.rng;
    this.lastExplosionT = G?.time ? G.time.t : 0;
    this.lastExplosionX = x;
    this.lastExplosionZ = z;
    // flash disc (short, bright core that bloom picks up)
    this.spawn(this.add, x, y + 0.4 * size, z, 0, 0, 0, 0.12, 1.8 * size, 4.0 * size, 0.85, 1.6, 3.0, 2.4, 1.6, 2.2, 1.0, 0.3, TEX_SOFT, 0, 0, 0, 0, GROUND_NONE);
    // fireball
    for (let i = 0; i < 30; i++) {
      const ax = this.rnd(-1, 1), ay = this.rnd(-0.2, 1.2), az = this.rnd(-1, 1);
      const l = Math.hypot(ax, ay, az) || 1;
      const sp = this.rnd(1.5, 6) * size;
      const s0 = this.rnd(1.0, 1.8) * size;
      this.spawn(
        this.add,
        x + (ax / l) * 0.4 * size, y + 0.3 + (ay / l) * 0.4 * size, z + (az / l) * 0.4 * size,
        (ax / l) * sp, (ay / l) * sp + 1.5, (az / l) * sp,
        this.rnd(0.45, 0.95),
        s0, s0 * 1.9, 1, 1.4,
        3.2, 1.5, 0.35, 1.0, 0.16, 0.02,
        TEX_PUFF, R() * 6.28, this.rnd(-1.5, 1.5),
        -2.5, 2.2, GROUND_NONE,
      );
    }
    // smoke column
    for (let i = 0; i < 40; i++) {
      const ax = this.rnd(-1, 1), az = this.rnd(-1, 1);
      const l = Math.hypot(ax, az) || 1;
      const sp = this.rnd(0.5, 2.5) * size;
      const s0 = this.rnd(0.9, 1.6) * size;
      const g = this.rnd(0.12, 0.2);
      this.spawn(
        this.nrm,
        x + (ax / l) * 0.6 * size, y + 0.2 + this.rnd(0, 1.2) * size, z + (az / l) * 0.6 * size,
        (ax / l) * sp, 1.6 + this.rnd(0, 3.2) * size, (az / l) * sp,
        this.rnd(2.2, 3.8),
        s0, s0 * 3.2, 0.66, 1.3,
        g, g, g, g + 0.3, g + 0.3, g + 0.31,
        TEX_PUFF, R() * 6.28, this.rnd(-0.8, 0.8),
        -0.7, 1.2, GROUND_NONE,
      );
    }
    // sparks + embers
    this.sparks(x, y + 0.3, z, 60, 0, 0.35, 0, 6 * size, 15 * size, 0.5, 1.2);
    // debris
    this.emit('debris', x, y + 0.2, z, 15, 0, 1, 0);
    // light flash (pooled, 3 max): take the one that is furthest into its fade
    let li = 0;
    for (let i = 1; i < this.lights.length; i++) if (this.lightAge[i] > this.lightAge[li]) li = i;
    const light = this.lights[li];
    light.position.set(x, y + 1.2, z);
    light.distance = 24 + 18 * size;
    this.lightPeak[li] = 180 * size;
    light.intensity = this.lightPeak[li];
    this.lightAge[li] = 0;
    if (G?.cam) G.cam.shake(size * 0.5, 0.5);
  }

  tracer(ax: number, ay: number, az: number, bx: number, by: number, bz: number): void {
    const i = this.tracerNext;
    this.tracerNext = (i + 1) % TRACER_MAX;
    const o = i * 6;
    const p = this.tracerPos;
    p[o] = ax;
    p[o + 1] = ay;
    p[o + 2] = az;
    p[o + 3] = bx;
    p[o + 4] = by;
    p[o + 5] = bz;
    this.tracerAge[i] = 0;
    this.tracerAlive++;
    this.writeTracerColor(i, 1);
    (this.tracerLine.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  private writeTracerColor(i: number, k: number): void {
    const c = this.tracerCol;
    const o = i * 6;
    // muzzle end brighter than the far end
    c[o] = 3.0 * k;
    c[o + 1] = 2.6 * k;
    c[o + 2] = 1.6 * k;
    c[o + 3] = 1.6 * k;
    c[o + 4] = 1.3 * k;
    c[o + 5] = 0.7 * k;
  }

  // --------------------------------------------------------------- update ----

  update(dt: number): void {
    dt *= this.timeScale;
    // perspective point-size scale (device pixels)
    const size = G.renderer.getDrawingBufferSize(this.tmpSize);
    const fov = (G.camera.fov * Math.PI) / 180;
    const scale = size.y / (2 * Math.tan(fov / 2));
    this.add.material.uniforms.uScale.value = scale;
    this.nrm.material.uniforms.uScale.value = scale;

    this.add.update(dt);
    this.nrm.update(dt);

    // tracers
    if (this.tracerAlive > 0) {
      let alive = 0;
      for (let i = 0; i < TRACER_MAX; i++) {
        const a = this.tracerAge[i];
        if (a > TRACER_LIFE) continue;
        const na = a + dt;
        this.tracerAge[i] = na;
        if (na > TRACER_LIFE) {
          this.writeTracerColor(i, 0);
        } else {
          alive++;
          this.writeTracerColor(i, 1 - na / TRACER_LIFE);
        }
      }
      this.tracerAlive = alive;
      (this.tracerLine.geometry.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
      this.tracerLine.visible = true;
    } else if (this.tracerLine.visible) {
      this.tracerLine.visible = false;
    }

    // explosion lights
    for (let i = 0; i < this.lights.length; i++) {
      const a = this.lightAge[i];
      if (a > 0.4) continue;
      const na = a + dt;
      this.lightAge[i] = na;
      const k = na >= 0.4 ? 0 : 1 - na / 0.4;
      this.lights[i].intensity = this.lightPeak[i] * k * k;
    }
  }

  /** number of live particles (debug / tests) */
  get count(): number {
    return this.add.n + this.nrm.n;
  }

  dispose(): void {
    this.scene.remove(this.add.points, this.nrm.points, this.tracerLine, ...this.lights);
  }
}
